var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import { executeConfig } from '@core/context/useExecuteRequest';
/**
 * Does this deployment have the feature at all?
 *
 * uxp ships from a CDN and updates every deployment at once; the server deploys per account and lags.
 * So a bundle routinely talks to a server whose System app predates the service it wants to call, and
 * the frontend has to notice rather than call into a void — or, worse, show UI that cannot work.
 *
 * There is no version handshake to ask instead: nothing in the bootstrap context carries a platform
 * version, and `AccountAppMap.Version` is the folder name ("5.0") on current and stale servers alike.
 * Probing is the only honest signal, and the shape of the answer is what makes it reliable — see
 * `isMissingFeatureError`.
 */
// A batch item the server rejected comes back as a plain string on an HTTP 200 response; a transport
// failure (network down, 401, the batch endpoint itself gone) arrives as an Error. Only the former says
// anything about what this deployment supports.
//
// These are the messages the server produces when an app, service, model or action is absent, each
// traced to its throw site in iviva-net-core:
//   invalid service                       SDApp.LoadServiceDirect      — app present, service unknown
//   not enabled for this account          SDAppNotFoundException       — app not installed
//   given key was not present in the ...  SDAccount.GetModelServiceWrapper — model-scoped service, model absent
//   unable to find app                                                 — app carries no models at all
//   no such model / no such action        MM.cs, ModelActor.cs         — Lucy model action
//   unknown action:                       ModelActor.cs                — Lucy action, alternate dispatch
//
// Matched as substrings, not prefixes: the message is sometimes wrapped before it reaches us.
const MISSING_FEATURE_ERRORS = [
    'invalid service',
    'not enabled for this account',
    'given key was not present in the dictionary',
    'unable to find app',
    'no such model',
    'no such action',
    'unknown action:',
];
/**
 * @export
 * Whether an error says the *server* does not have the thing, as opposed to the call having failed.
 *
 * Accepts whatever the execute layer hands back: the raw `error` (a string from the server, an Error
 * from the transport), or the whole `ExecutionResult`. Anything that is not a non-empty server string
 * — a network blip, a 401, a genuine failure inside a service that does exist — is not a verdict about
 * this deployment and returns false.
 *
 * Note a batch item runs without per-service role validation, so a permission denial never reaches
 * here: this answers "is it installed", never "may I use it".
 */
export function isMissingFeatureError(error) {
    const value = error && typeof error === 'object' && 'error' in error ? error.error : error;
    if (typeof value !== 'string' || !value)
        return false;
    const message = value.toLowerCase();
    return MISSING_FEATURE_ERRORS.some(m => message.indexOf(m) >= 0);
}
/** `service:System:MediaItem:MyMedia` — what the call targets, never its parameters. */
function defaultKey(config) {
    var _a, _b, _c;
    switch (config.type) {
        case 'service': return `service:${config.app}:${config.service}`;
        case 'action': return `action:${config.model}:${config.action}`;
        case 'collection': return `collection:${config.model}:${config.collection}`;
        case 'microservice': return `microservice:${(_a = config.config) === null || _a === void 0 ? void 0 : _a.serviceName}:${(_b = config.config) === null || _b === void 0 ? void 0 : _b.route}`;
        case 'api': return `api:${(_c = config.config) === null || _c === void 0 ? void 0 : _c.url}`;
        case 'query': return 'query';
        default: return 'unknown';
    }
}
// One answer per feature per page load, shared by every probe and call below so the two can never
// disagree. A definite yes or no sticks; an inconclusive answer evicts itself so the next caller retries.
const answers = new Map();
/**
 * @export
 * Whether this deployment has the feature the config targets, cached for the page's lifetime.
 *
 * The probe is a real request, so pass a **harmless** one — the cheapest read the feature needs is
 * usually both the honest probe and a call you were going to make anyway (listing one row, say). For a
 * feature whose only call is a write, use `executeIfAvailable` instead.
 *
 * Optimistic on anything that isn't a definite "no such thing": wrongly hiding a feature for a whole
 * page load because of a network blip is worse than showing one error message.
 */
export function isFeatureAvailable(context, config, options) {
    const key = (options === null || options === void 0 ? void 0 : options.key) || defaultKey(config);
    let answer = answers.get(key);
    if (!answer) {
        answer = executeConfig(context, config)
            .then(({ error }) => {
            if (!error)
                return true;
            if (isMissingFeatureError(error))
                return false;
            answers.delete(key);
            return true;
        })
            .catch(() => {
            answers.delete(key);
            return true;
        });
        answers.set(key, answer);
    }
    return answer;
}
/**
 * @export
 * `null` until the answer arrives, so a caller can hold the affected UI back rather than render
 * something that is about to disappear. Only asks while `active`.
 */
export function useFeatureAvailable(config, options) {
    const context = useUXPContext();
    const [available, setAvailable] = React.useState(null);
    const active = (options === null || options === void 0 ? void 0 : options.active) !== false;
    const key = (options === null || options === void 0 ? void 0 : options.key) || defaultKey(config);
    React.useEffect(() => {
        if (!active || available !== null)
            return;
        let cancelled = false;
        isFeatureAvailable(context, config, { key }).then(result => { if (!cancelled)
            setAvailable(result); });
        return () => { cancelled = true; };
        // The config is rebuilt on every render at most call sites; the key is what identifies it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, available, context, key]);
    return available;
}
/**
 * @export
 * Run a call that may not exist on this server, and stop calling once it is known not to.
 *
 * For features that cannot be probed harmlessly because the call *is* the feature — recording an
 * event, say. The first call is the real one; if the server answers that it has no such thing, the
 * feature is latched off and later calls resolve `null` without a request. Resolves `null` when the
 * call was skipped, so `null` means "not attempted", not "failed".
 *
 * The latch matters on the server too: an old deployment logs an exception for every rejected batch
 * item, so an unguarded fire-and-forget call fills its subsystem log with the user's browsing.
 */
export function executeIfAvailable(context, config, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const key = (options === null || options === void 0 ? void 0 : options.key) || defaultKey(config);
        const known = answers.get(key);
        if (known && (yield known) === false)
            return null;
        const result = yield executeConfig(context, config);
        if (isMissingFeatureError(result.error)) {
            answers.set(key, Promise.resolve(false));
            return null;
        }
        // Only a definite success is worth remembering; a transient failure says nothing either way.
        if (!result.error)
            answers.set(key, Promise.resolve(true));
        return result;
    });
}
